<template>
  <div
    :class="[
      $style.base,
      {
        [$style.inline]: inline,
        [$style.disabled]: disabled
      }
    ]"
  >
    <BaseInputLabel
      v-if="label"
      :text="label"
      v-test="'_base-radio-label'"
    />
    <div
      :class="$style.options"
      v-test="'_base-radio-options'"
    >
      <div
        v-for="(option, index) in options"
        :key="index"
        :class="$style.option"
        v-test="'_base-radio-option'"
      >
        <input
          :id="`${uniqueId}-${index}`"
          v-model="localValue"
          type="radio"
          :value="option.value"
          :name="uniqueId"
          :disabled="disabled"
          :class="$style.input"
        >
        <label
          :for="`${uniqueId}-${index}`"
          :class="$style.label"
          v-test="'_base-radio-option-label'"
        >
          {{ filters.capitalize(option.label) }}
        </label>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import filters from '@/filters';

export default defineComponent({
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Number, Boolean],
      required: false,
    },
    options: {
      type: Array,
      required: true,
      validator: (value) =>
        !value.find(
          (item) => item.label === undefined || item.value === undefined,
        ),
    },
    label: {
      type: String,
      required: false,
    },
    inline: {
      type: Boolean,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  setup() {
    return {
      filters,
    };
  },
  computed: {
    uniqueId() {
      const uid = Math.round(Math.random() * 100000).toString();
      return `radio-${uid}`;
    },
    localValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  width: fit-content;
}

.input {
  // Visually hidden but still focusable, so the radio works with the keyboard
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;

  // The dot is a 2px inset ring in the dot's background colour over the
  // selection fill, so it follows the theme instead of being a fixed white
  // border. Border and dot darken together on hover and press.
  &:checked ~ .label:before {
    border-width: 2px;
    border-color: var(--selection);
    background-color: var(--selection);
    box-shadow: inset 0 0 0 2px var(--surface-primary);
  }

  &:checked ~ .label:hover:before,
  &:checked ~ .label:active:before {
    border-color: var(--selection-hover);
    background-color: var(--selection-hover);
  }

  &:focus-visible ~ .label:before {
    box-shadow: 0 0 0 4px var(--focus-ring);
  }

  &:checked:focus-visible ~ .label:before {
    box-shadow:
      inset 0 0 0 2px var(--surface-primary),
      0 0 0 4px var(--focus-ring);
  }

  .base.disabled &:checked ~ .label:before {
    border-color: var(--text-placeholder);
    background-color: var(--text-placeholder);
    box-shadow: inset 0 0 0 2px var(--neutral-softer);
  }
}

.options {
  display: flex;

  .base.inline & {
    flex-wrap: wrap;
    gap: $spacing;
  }

  .base:not(.inline) & {
    flex-direction: column;
    gap: $spacing * 0.5;
  }

  .base.disabled & {
    pointer-events: none;
  }
}

.option {
  position: relative;
}

.label {
  display: inline-flex;
  align-items: center;

  &:before {
    content: '';
    width: $spacing;
    height: $spacing;
    border-radius: 50%;
    border: 1px solid var(--border-strong);
    flex-shrink: 0;
    margin-right: $spacing * 0.5;
    background-color: var(--surface-primary);
    transition:
      background-color 0.12s ease,
      border-color 0.12s ease;
  }

  &:hover:before {
    border-color: var(--selection);
  }

  &:active:before {
    border-color: var(--selection-hover);
  }

  .base.disabled & {
    color: var(--text-placeholder);

    &:before {
      border-color: var(--border);
      background-color: var(--neutral-softer);
    }
  }
}
</style>
